import { useState, useRef, useEffect, type ReactNode } from 'react';

interface TooltipProps {
  text: string;
  children: ReactNode;
  position?: 'top' | 'bottom';
}

export default function Tooltip({ text, children, position = 'top' }: TooltipProps) {
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (visible && triggerRef.current && tooltipRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      const left = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
      const top =
        position === 'top'
          ? triggerRect.top - tooltipRect.height - 6
          : triggerRect.bottom + 6;

      // Clamp horizontal to viewport
      const clampedLeft = Math.max(8, Math.min(left, window.innerWidth - tooltipRect.width - 8));
      setCoords({ top, left: clampedLeft });
    }
  }, [visible, position]);

  return (
    <div
      ref={triggerRef}
      className="inline-flex"
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => { setVisible(false); setCoords(null); }}
    >
      {children}
      {visible && (
        <div
          ref={tooltipRef}
          className="fixed z-[9999] pointer-events-none"
          style={{
            top: coords?.top ?? -9999,
            left: coords?.left ?? -9999,
            opacity: coords ? 1 : 0,
          }}
        >
          <div className="bg-gray-900 text-white text-xs font-medium px-2.5 py-1.5 rounded-md shadow-lg whitespace-nowrap max-w-xs">
            {text}
          </div>
        </div>
      )}
    </div>
  );
}
